import { Turnstile } from '@marsidev/react-turnstile'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@rekrytering/ui/components/select'
import { Textarea } from '@rekrytering/ui/components/textarea'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { env } from '@/env.client'

type Topic =
  | 'bug'
  | 'technical_issue'
  | 'billing'
  | 'general'
  | 'feature_request'

type Severity = 'low' | 'medium' | 'high' | 'critical'

const siteUrl = env.VITE_CONVEX_SITE_URL

export function AnonymousContactForm() {
  const { t } = useTranslation()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [topic, setTopic] = useState<Topic>('general')
  const [severity, setSeverity] = useState<Severity>('low')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [submittedTicketId, setSubmittedTicketId] = useState<string | null>(
    null,
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const turnstileTokenRef = useRef<string | undefined>(undefined)

  const canSubmit =
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    message.trim().length > 0 &&
    !submitting

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      const response = await fetch(`${siteUrl}/support/ticket/anonymous`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          topic,
          severity,
          subject: subject.trim(),
          message: message.trim(),
          turnstileToken: turnstileTokenRef.current ?? '',
        }),
      })

      if (!response.ok) {
        setError(t('support.contactForm.errorGeneric'))
        return
      }

      setSubmittedTicketId('submitted')
    } finally {
      setSubmitting(false)
    }
  }

  if (submittedTicketId) {
    return (
      <div className="flex flex-col gap-2">
        <p className="font-medium">{t('support.contactForm.successTitle')}</p>
        <p className="text-muted-foreground text-sm">
          {t('support.contactForm.successMessage')}
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">
            {t('support.contactForm.nameLabel')}
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('support.contactForm.namePlaceholder')}
            maxLength={200}
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">
            {t('support.contactForm.emailLabel')}
          </label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('support.contactForm.emailPlaceholder')}
            maxLength={200}
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">
            {t('settings.contactSupport.topicLabel')}
          </label>
          <Select value={topic} onValueChange={(v) => setTopic(v as Topic)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bug">
                {t('settings.contactSupport.topicBug')}
              </SelectItem>
              <SelectItem value="technical_issue">
                {t('settings.contactSupport.topicTechnicalIssue')}
              </SelectItem>
              <SelectItem value="billing">
                {t('settings.contactSupport.topicBilling')}
              </SelectItem>
              <SelectItem value="general">
                {t('settings.contactSupport.topicGeneral')}
              </SelectItem>
              <SelectItem value="feature_request">
                {t('settings.contactSupport.topicFeatureRequest')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">
            {t('settings.contactSupport.severityLabel')}
          </label>
          <Select
            value={severity}
            onValueChange={(v) => setSeverity(v as Severity)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">
                {t('settings.contactSupport.severityLow')}
              </SelectItem>
              <SelectItem value="medium">
                {t('settings.contactSupport.severityMedium')}
              </SelectItem>
              <SelectItem value="high">
                {t('settings.contactSupport.severityHigh')}
              </SelectItem>
              <SelectItem value="critical">
                {t('settings.contactSupport.severityCritical')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium">
          {t('settings.contactSupport.subjectLabel')}
        </label>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={t('settings.contactSupport.subjectPlaceholder')}
          maxLength={200}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium">
          {t('settings.contactSupport.messageLabel')}
        </label>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('settings.contactSupport.messagePlaceholder')}
          rows={5}
          maxLength={2000}
          className="resize-none"
          required
        />
      </div>

      <Turnstile
        siteKey={env.VITE_TURNSTILE_SITE_KEY}
        onSuccess={(token) => {
          turnstileTokenRef.current = token
        }}
        onExpire={() => {
          turnstileTokenRef.current = undefined
        }}
      />

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Button type="submit" disabled={!canSubmit}>
        {submitting
          ? t('settings.contactSupport.submitting')
          : t('settings.contactSupport.submit')}
      </Button>
    </form>
  )
}
